import React, { Component } from "react";
import PropTypes from "prop-types";
import moment from "moment";
import { isEmpty } from "lodash";
import { Icons } from "prefab";
import { FORM_FIELD_TYPE, REASON_TYPES } from "../../../../constants";
import RightPanel from "../../../common/RightPanel";
import LocalizedButton from "../../../common/LocalizedButton";
import Form from "../../../common/Form";
import InfoBlock from "../../../common/InfoBlock";
import * as constants from "../../../../constants";
import { getAllReasonsByType } from "../../../../api";
import formatValidityPeriodDisplay from "../../../../utils/formatValidityPeriodDisplay";
import BulkActionButtonsStyles from "../../../../styles/BulkActionButtons.module.scss";
import styles from "../../../../styles/Campaigns/PauseOrResumeActions.module.scss";

const { InfoFilledIcon } = Icons;

const SCHEDULE_TYPE = { IMMEDIATE: "immediate", SELECT_DATE_RANGE: "selectDateRange" };

export default class PauseOrResumeActions extends Component {
  constructor(props) {
    super(props);
    this.state = {
      period: {
        from: props.isEdit ? props.period.fromDate : null,
        to: props.isEdit ? props.period.toDate : null,
      },
      scheduleType: SCHEDULE_TYPE.SELECT_DATE_RANGE,
      hasError: false,
      reason: null,
      notes: "",
      showEdit: props.showEdit,
    };
  }

  static propTypes = {
    showEdit: PropTypes.bool.isRequired,
    isOpen: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    isEdit: PropTypes.bool,
    onPauseOrResumeAction: PropTypes.func.isRequired,
    reasonType: PropTypes.string,
  };

  static defaultProps = {
    isEdit: false,
    period: { fromDate: null, toDate: null },
    selectionInfo: null,
    validity: null,
  };

  componentWillUnmount = () => {
    this.cancelAction();
  };

  cancelAction = () => {
    this.setState({
      hasError: false,
      period: {
        from: null,
        to: null,
      },
      scheduleType: SCHEDULE_TYPE.SELECT_DATE_RANGE,
      showEdit: false,
    });
    this.props.onClose();
  };

  showEditForm = (show = false) => {
    this.setState({
      showEdit: show,
      period: {
        from: null,
        to: null,
      },
    });
  };

  //check in selected campaign list if entered fromDate isBefore any campaign's start Date
  validateMinDate = (fromDate) => {
    const { selected, validity } = this.props;
    if (!fromDate) return true;
    return (
      selected.findIndex((campaign) => {
        const validityData = validity ? validity : campaign.validity;
        return moment(fromDate, "YYYY-MM-DD").isBefore(
          moment(validityData?.fromDate, "YYYY-MM-DD")
        );
      }) > -1
    );
  };

  //check in selected campaign list if entered toDate isAfter any campaign's end Date
  validateMaxDate = (toDate) => {
    const { selected, validity } = this.props;

    if (!moment(toDate, "YYYY-MM-DD").isValid()) return false;
    return (
      selected.findIndex((campaign) => {
        const validityData = validity ? validity : campaign.validity;
        return moment(toDate, "YYYY-MM-DD").isAfter(validityData?.toDate);
      }) > -1
    );
  };

  save = () => {
    const { period, scheduleType, reason, notes } = this.state;
    const { isPaused } = this.props;
    const isImmediate = scheduleType === SCHEDULE_TYPE.IMMEDIATE;
    const effectiveFrom = isImmediate ? moment().format("YYYY-MM-DD") : period.from;
    const effectivePeriod = { ...period, from: effectiveFrom };
    if (
      this.validateMinDate(effectiveFrom) ||
      (isPaused && (!effectivePeriod.to || isEmpty(reason)))
    ) {
      this.setState({ hasError: true });
      return;
    }
    //Resume has no schedule-type selector; treat a same-day Resume From as immediate
    const isResumeImmediate = !isPaused && moment(effectiveFrom).isSame(moment(), "day");
    this.props.onPauseOrResumeAction({
      ...effectivePeriod,
      reason,
      notes,
      isImmediate: isImmediate || isResumeImmediate,
    });
    this.cancelAction();
  };

  onDateInputChange = (value, accessor) => {
    const { period } = this.state;
    this.setState({
      period: { ...period, [accessor]: value },
    });
  };

  inputOnChange = (value, accessor) =>
    this.setState({
      [accessor]: value,
    });

  getConsumedCampaignList = () => {
    const { selected } = this.props;
    const { CAMPAIGN_STATUSES } = constants;

    return selected.filter((campaign) => campaign.status === CAMPAIGN_STATUSES.CONSUMED);
  };

  renderConsumedCampaignInfo = () => {
    const { selected, history } = this.props;
    const consumedCampaignList = this.getConsumedCampaignList();

    if (selected.length === 0) return null;

    if (consumedCampaignList.length) {
      return (
        <>
          <p className={styles.actionInfo}>
            {consumedCampaignList.length > 1
              ? `One of the Selected Campaign has consumed its maximum play`
              : `The Above Campaign has consumed its max play Please Increase its maximum play`}
          </p>
          {consumedCampaignList.length === 1 && (
            <LocalizedButton
              className={`${styles.editButton}`}
              iconName="EditIcon"
              text="Button.editCampaign"
              onClick={(e) => {
                this.cancelAction();
                history.push(`/campaigns/campaigns/${consumedCampaignList[0].id}/details`);
              }}
            />
          )}
        </>
      );
    }
  };

  render = () => {
    const {
      isOpen,
      header,
      selectionInfo,
      isPaused,
      isEdit,
      actionType,
      validity,
      selected,
    } = this.props;
    const { period, scheduleType, hasError, showEdit, reason, notes } = this.state;
    const consumedCampaigns = this.getConsumedCampaignList();
    const isImmediate = scheduleType === SCHEDULE_TYPE.IMMEDIATE;
    const isMedia = this.props.reasonType === REASON_TYPES.mediaCancel;

    const todayStart = moment().startOf("day").toDate();
    const minDateMoment = validity?.fromDate
      ? moment(validity.fromDate).startOf("day")
      : moment.max(selected.map((campaign) => moment(campaign?.validity?.fromDate).startOf("day")));
    let minDate = minDateMoment ? minDateMoment.toDate() : undefined;
    minDate = minDate && minDate > todayStart ? minDate : todayStart;

    const maxDateMoment = validity?.toDate
      ? moment(this.props.validity.toDate).endOf("day")
      : moment.min(selected.map((campaign) => moment(campaign?.validity?.toDate).endOf("day")));
    const maxDate = maxDateMoment ? maxDateMoment.toDate() : undefined;

    const fromDateMin = minDate;
    const periodToEnd = period.to ? moment(period.to).endOf("day").toDate() : undefined;
    const fromDateMax = periodToEnd && maxDate > periodToEnd ? periodToEnd : maxDate;

    //Pause From must start from tomorrow when scheduling for a future date range
    const tomorrowStart = moment().add(1, "day").startOf("day").toDate();
    const pauseFromMin = minDate && minDate > tomorrowStart ? minDate : tomorrowStart;
    const pauseFromMax = fromDateMax;

    const toDateMin = isImmediate
      ? todayStart
      : period.from
      ? moment(period.from).startOf("day").toDate()
      : pauseFromMin;
    const toDateMax = maxDate;

    const pauseCalloutText = isImmediate
      ? isMedia
        ? "This media will pause before the next scheduled show begins."
        : "The campaign will pause before the next scheduled show begins."
      : isMedia
      ? 'This media will be paused from the start of the selected "Pause From" date until the end of the selected "Pause Till" date, based on the theater\'s local time.'
      : 'The campaign will be paused from the start of the selected "Pause From" date until the end of the selected "Pause Till" date, based on the theater\'s local time.';

    return (
      <RightPanel
        isOpen={isOpen}
        onClose={() => this.cancelAction()}
        header={header}
        showFooter={true}
        primaryButtonProps={
          isEdit
            ? {
                text: "Button.save",
                onClick: this.save,
                className: styles.save,
                disabled: consumedCampaigns.length > 0 || !showEdit,
              }
            : {
                text: isPaused
                  ? isMedia
                    ? "Button.pauseMedia"
                    : "Button.pauseCampaign"
                  : isMedia
                  ? "Button.resumeMedia"
                  : "Button.resumeCampaign",
                onClick: this.save,
                disabled: consumedCampaigns.length > 0 || !showEdit,
              }
        }
        secondaryButtonProps={{ text: "Button.cancel", onClick: this.cancelAction }}
      >
        <div className={styles.panelBody}>
          <div className={styles.formSection}>
            {!isEmpty(selectionInfo) && (
              <div className={BulkActionButtonsStyles.selectionInfoContainer}>
                <div className={BulkActionButtonsStyles.selectionInfo}>
                  <span>{selectionInfo.count}</span> {selectionInfo.label} Selected
                </div>
              </div>
            )}
            {selected?.length === 1 && (
              <InfoBlock
                hideHeader
                cardType="secondary"
                className={styles.selectedItemInfo}
                config={[
                  {
                    title: isMedia ? "Media Name & ID" : "Campaign Name & ID",
                    value: `${selected[0].name} (${
                      isMedia ? selected[0].mediaNumber : selected[0].campaignNumber
                    })`,
                    size: { lg: 12, md: 12, sm: 12 },
                  },
                  {
                    title: "Validity",
                    value: formatValidityPeriodDisplay(selected[0].validity, "DD MMM YYYY"),
                    size: { lg: 12, md: 12, sm: 12 },
                  },
                ]}
              />
            )}
            {this.renderConsumedCampaignInfo()}
            {consumedCampaigns.length === 0 && isEdit && !showEdit && (
              <>
                <p className={styles.actionInfo}>
                  Campaigns have {actionType} action at different dates
                </p>
                <LocalizedButton
                  className={`${styles.removeButton}`}
                  iconName="CancelFilledIcon"
                  text="Button.removeDurations"
                  onClick={(e) => this.showEditForm(true)}
                />
              </>
            )}
            {consumedCampaigns.length === 0 && (!isEdit || showEdit) && (
              <Form
                config={[
                  {
                    type: FORM_FIELD_TYPE.RADIO,
                    title: "Schedule Type",
                    isHidden: !isPaused,
                    radioList: [
                      { label: "Immediate", value: SCHEDULE_TYPE.IMMEDIATE },
                      { label: "Select Date Range", value: SCHEDULE_TYPE.SELECT_DATE_RANGE },
                    ],
                    value: scheduleType,
                    onChange: (v) => this.setState({ scheduleType: v, hasError: false }),
                  },
                  {
                    type: FORM_FIELD_TYPE.DATE,
                    title: "Pause From",
                    isHidden: !isPaused || isImmediate,
                    error:
                      hasError &&
                      !isImmediate &&
                      (!period.from || this.validateMinDate(period.from)),
                    errorMessage: "Enter Valid From Date",
                    invalidDateMessage: "Invalid From Date",
                    onChange: (v) => {
                      this.onDateInputChange(moment(v).format("YYYY-MM-DD"), "from");
                    },
                    value: period.from ? new Date(period.from) : null,
                    placeholder: "Select Start Date",
                    minDate: pauseFromMin,
                    defaultValue: pauseFromMin,
                    maxDate: pauseFromMax,
                    disabled: pauseFromMin > pauseFromMax,
                  },
                  {
                    type: FORM_FIELD_TYPE.DATE,
                    isHidden: !isPaused,
                    title: "Pause Till",
                    error: hasError && (!period.to || this.validateMaxDate(period.to)),
                    errorMessage: "Enter Valid To Date",
                    invalidDateMessage: "Invalid To Date",
                    onChange: (v) => {
                      this.onDateInputChange(moment(v).format("YYYY-MM-DD"), "to");
                    },
                    value: period.to ? new Date(period.to) : null,
                    placeholder: "Select End Date",
                    minDate: toDateMin,
                    defaultValue: period.from ? new Date(period.from) : new Date(),
                    maxDate: toDateMax,
                    disabled: toDateMin > toDateMax,
                  },
                  {
                    type: FORM_FIELD_TYPE.DATE,
                    isHidden: isPaused,
                    title: "Resume From",
                    error: hasError && (!period.from || this.validateMinDate(period.from)),
                    errorMessage: "Enter Valid From Date",
                    invalidDateMessage: "Invalid From Date",
                    onChange: (v) => {
                      this.onDateInputChange(moment(v).format("YYYY-MM-DD"), "from");
                    },
                    value: period.from ? new Date(period.from) : null,
                    placeholder: "DD MMM YYYY",
                    minDate: fromDateMin,
                    defaultValue: fromDateMin,
                    maxDate: fromDateMax,
                    disabled: fromDateMin > fromDateMax,
                  },
                  {
                    type: FORM_FIELD_TYPE.API_SELECT,
                    id: "reason",
                    title: "Reason",
                    label: "",
                    isHidden: !isPaused,
                    placeholder: "Select Reason for Pause",
                    fetchAction: () =>
                      getAllReasonsByType(this.props.reasonType || REASON_TYPES.campaignCancel),
                    parseResults: (response) => {
                      return response.data.map((item) => ({
                        id: item.id,
                        value: item.reason,
                      }));
                    },
                    selected: (list) => list.filter((item) => item.id === reason),
                    singleSelect: true,
                    showLabelInButton: false,
                    onSelect: ([value]) => {
                      this.inputOnChange(value.id, "reason");
                    },
                    error: hasError && !reason,
                    errorMessage: "Reason Cannot be Empty",
                  },
                  {
                    id: "comments",
                    type: FORM_FIELD_TYPE.TEXT_AREA,
                    title: "Comments",
                    placeholder: "Enter Comments",
                    rows: 3,
                    value: notes,
                    onChange: (e) => this.inputOnChange(e.target.value, "notes"),
                  },
                ]}
              />
            )}
          </div>
          {isPaused && (
            <div className={styles.calloutInfo}>
              <InfoFilledIcon />
              <span>{pauseCalloutText}</span>
            </div>
          )}
        </div>
      </RightPanel>
    );
  };
}
